import { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import cryptoRandomString from 'crypto-random-string';
// Types
import QuizTypes from 'types/QuizTypes';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Async
import { getConsentDocuments, getConsentLinksDocuments } from 'store/consent/consentAsync';
import { getPlayer } from 'store/player/playerAsync';
// Selectors
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectAnalyticData, selectResultId, selectUserId } from 'store/auth/authSelectors';
import { selectConsentDocuments, selectConsentLinksDocuments } from 'store/consent/consentSelectors';
import { selectIPLocationInfo } from 'store/player/playerSelectors';
// Actions
import { setPlayerData } from 'store/rokt/roktSlice';
import { setAnalyticData } from 'store/auth/authSlice';
// Hooks
import useToggle from 'hooks/useToggle';
// Dialogs
import ConsentContentDialog from 'dialogs/ConsentContent.dialog';
// Components
import { Input, Checkbox } from 'components/Controls';
import { Button } from 'components/Buttons';
import Phone from 'components/Phone';
import { isValidPhoneNumber } from 'react-phone-number-input';
import AppLoader from 'components/AppLoader';
// Services
import StorageService from 'services/Storage.service';
// i18next
import { Trans, useTranslation } from 'react-i18next';
// Utilities
import { isNumeric, renderWithVariables, textFromNormalToCamelCase } from 'utilities/Utilities';

// eslint-disable-next-line
const emailPattern = /(?!.*\.{2})^([a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+(\.[a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+)*|"((([ \t]*\r\n)?[ \t]+)?([\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*(([ \t]*\r\n)?[ \t]+)?")@(([a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.)+([a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.?$/i;

interface IFormData {
  firstName: string;
  lastName: string;
  email: string;
  yearOfBirth: string;
  phoneNumber?: string;
  consents?: string[];
  customFields?: Record<string, string>;
};

type Props = {
  onAsyncRegisterUser: (data:any, doRegister?:boolean, doRedirect?:boolean) => void;
  loading: boolean;
}

const PublicForm:FC<Props> = ({
  // Props
  onAsyncRegisterUser, loading
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // State
  const quiz:any = useAppSelector(selectQuiz);
  const userAnalyticData = useAppSelector(selectAnalyticData);
  const resultId = useAppSelector(selectResultId);
  const userId = useAppSelector(selectUserId);
  const consentDocuments = useAppSelector(selectConsentDocuments);
  const consentLinksDocuments = useAppSelector(selectConsentLinksDocuments);
  const ipLocationInfo = useAppSelector(selectIPLocationInfo);

  const [ selectedContent, setSelectedContent ] = useState<string>('');

  const { open:openContentDialog, toggle:toggleContentDialog } = useToggle();

  const handleOpenContentDialog = (content: string | undefined) => {
    setSelectedContent(content || '');
    toggleContentDialog();
  }

  const [ qwid, setQwid ] = useState<string | null>(StorageService.getQwid());
  const [ hasPublicData, setHasPublicData ] = useState<boolean | null>(null);
  const [ isSubmitted, setIsSubmitted ] = useState(false);
  const [ formData, setFormData ] = useState<IFormData>({
    firstName: '',
    lastName: '',
    email: '',
    yearOfBirth: '',
    phoneNumber: '',
    consents: [],
    customFields: {}
  });
  const [ errors, setErrors ] = useState({
    firstName: '',
    lastName: '',
    email: '',
    yearOfBirth: '',
    phoneNumber: ''
  });

  useEffect(() => {
    const hasErrors = Object.values(errors).some(v => v);
    if ( isSubmitted && !hasErrors ){
      let registerData:any = null;
      let analyticData:any = {
        consents: formData.consents
      }

      if (!qwid || !hasPublicData) {
        const randomString = cryptoRandomString({ length: 11 });
        registerData = {
          name: formData.firstName,
          surname: formData.lastName,
          email: formData.email,
          password: randomString,
          customFields: {}
        };
        if (formData.phoneNumber) registerData['phoneNumber'] = formData.phoneNumber;
        if (formData.customFields) {
          Object.keys(formData.customFields).forEach(key => {
            const camelCasedKey = textFromNormalToCamelCase(key);
            registerData.customFields[camelCasedKey] = formData.customFields?.[key];
          });
        }
        analyticData = {
          ...analyticData,
          yearOfBirth: formData.yearOfBirth,
        };
      }

      if (quiz.registrationPage.showPriorQuiz && !userId) {
        dispatch(setAnalyticData(analyticData));
      }

      if (quiz.registrationPage.showPriorQuiz && !!userId) {
        analyticData = userAnalyticData;
      }

      if (!!qwid && hasPublicData) {
        registerData = null;
      }

      if (userId) {
        onAsyncRegisterUser({ analyticData });
      } else {
        onAsyncRegisterUser({ registerData, analyticData });
      }
      
      if (!qwid || !hasPublicData) {
        dispatch(setPlayerData({
          firstname: formData.firstName,
          lastname: formData.lastName,
          email: formData.email,
          birthyear: formData.yearOfBirth,
          phoneNumber: formData.phoneNumber,
          customFields: formData.customFields
        }));
      }
    }
    // eslint-disable-next-line
  }, [isSubmitted, errors]);

  const handleChangeCheckbox = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    if ( name === 'checkAll' ){
      setFormData((prevState) => ({
        ...prevState,
        consents: consentDocuments?.map(document => document._id)
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        consents: checked ? [...(prevState.consents || []), name] : prevState.consents?.filter((c:string) => c !== name)
      }));
    }
  }

  const handleChangeInput = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  }

  const handleChangeCustomFields = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      customFields: {
        ...prevState.customFields,
        [name]: value
      }
    }));
  }

  const handleChangePhone = (phone: string | undefined) => {
    setFormData((prevState) => ({
      ...prevState,
      phoneNumber: phone
    }));
  }

  const handleClick = () => {
    setIsSubmitted(true);
    if (qwid && hasPublicData) return;
    setErrors((prevState) => ({
      ...prevState,
      firstName: !formData.firstName.trim() ? t('validations.requiredField') : '',
      lastName: !formData.lastName.trim() ? t('validations.requiredField') : '',
      email: !formData.email.trim()
        ? t('validations.requiredField')
        : !emailPattern.test(formData.email)
          ? t('validations.emailNotValid')
          : ''
      ,
      phoneNumber: validatePhoneNumber(formData.phoneNumber),
      yearOfBirth: validateYearOfBirth(formData.yearOfBirth)
    }));
  }

  const validatePhoneNumber = (phone: string | undefined) => {
    if (phone && !isValidPhoneNumber(phone)) return t('validations.phoneNotValid');
    return '';
  }

  const validateYearOfBirth = (yearOfBirth:string) => {
    if ( !yearOfBirth ) return '';
    if ( !isNumeric(yearOfBirth) ) return t('validations.shouldBeNumeric');
    if ( yearOfBirth.length !== 4 ) return t('validations.yearNotCorrect');
    const currentYear = new Date().getFullYear();
    const yearToNum = Number(yearOfBirth);
    if ( yearToNum < 1920 || yearToNum > currentYear ) return t('validations.yearRangeNotCorrect');
    return '';
  }

  useEffect(() => {
    if (quiz.registrationPage.showPriorQuiz && !!userId && !!resultId) {
      setIsSubmitted(true);
    }
    // eslint-disable-next-line
  }, [resultId]);

  useEffect(() => {
    dispatch(getConsentDocuments(quiz.consents)).unwrap();
    dispatch(getConsentLinksDocuments(quiz.consentsLinks)).unwrap();
    // eslint-disable-next-line
  }, []);

  const resetQwid = () => {
    setQwid(null);
    StorageService.removeQwid();
  }

  const requiredConsentsIds = consentDocuments?.filter(documnent => documnent.required).map(doc => doc._id) || [];
  const countryCode = ipLocationInfo?.country;

  useEffect(() => {
    if (qwid) {
      dispatch(getPlayer(qwid))
        .then((data:any) => {
          const player = data.payload;
          if (player && player.email && !player.email.endsWith('@anonymous')) {
            setHasPublicData(true);
          } else {
            setHasPublicData(false);
          }
        })
        .catch(() => {
          setHasPublicData(false);
        });
    } else {
      setHasPublicData(false);
    }
    // eslint-disable-next-line
  }, [qwid]);

  if (!!qwid && hasPublicData === null) return <AppLoader fullScreen={false} />;
  return (
    <Fragment>
      {quiz.registrationPage.caption && (!qwid || !hasPublicData) && !userId ? (
        <h2 className="font-bold text-center px-4 text-primary text-xl mb-4">{quiz.registrationPage.caption}</h2>
      ) : null}
      {!!qwid && !userId && hasPublicData && (
        <div className="mb-2 text-center">
          <h2 className="text-lg mb-2 text-primary">{t('pages.register.alreadyRegistered')}</h2>
          <Button onClick={resetQwid}  className="mt-2 mb-4">
            {t('pages.register.registerNewUserButton')}
          </Button>
        </div>
      )}
      {[QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quiz.type) && quiz.spotPrizesEnabled && quiz.spotPrizes && quiz.spotPrizes.length && (!qwid || !hasPublicData) && !userId ? (
        <span className="block text-center font-bold mb-4 text-primary px-4">{t('pages.register.prizeHint', { prize: quiz.spotPrizes[0] })}</span>
      ) : null}
      {!userId && (
        <div className="px-4">
          {(!qwid || !hasPublicData) && (
            <Fragment>
              <Input
                className="bg-[#f2f2f2]"
                name="firstName" id="firstName" placeholder={t('labels.firstName')}
                value={formData.firstName}
                onChange={handleChangeInput}
                error={Boolean(errors.firstName)}
                errorText={errors.firstName}
              />
              <Input
                className="bg-[#f2f2f2]"
                name="lastName" id="lastName" placeholder={t('labels.lastName')}
                value={formData.lastName}
                onChange={handleChangeInput}
                error={Boolean(errors.lastName)}
                errorText={errors.lastName}
              />
              <Input
                className="bg-[#f2f2f2]"
                name="email" id="email" placeholder={t('labels.email')}
                value={formData.email}
                onChange={handleChangeInput}
                error={Boolean(errors.email)}
                errorText={errors.email}
              />
              {quiz.registrationPage?.showDateOfBirth && (
                <Input
                  className="bg-[#f2f2f2]"
                  name="yearOfBirth" id="yearOfBirth" placeholder={t('labels.yearOfBirth')}
                  value={formData.yearOfBirth}
                  onChange={handleChangeInput}
                  error={Boolean(errors.yearOfBirth)}
                  errorText={errors.yearOfBirth}
                />
              )}
              {quiz.registrationPage?.requirePhoneNumber && (
                <Phone
                  className="bg-[#f2f2f2]"
                  name="phoneNumber"
                  placeholder={t('labels.phoneNumber')}
                  value={formData.phoneNumber || ''}
                  onChange={handleChangePhone}
                  error={Boolean(errors.phoneNumber)}
                  errorText={errors.phoneNumber}
                  country={countryCode}
                />
              )}
              {quiz.registrationPage?.customFields?.map((field:string) => (
                <Input
                  key={field} name={field} id={field} placeholder={field}
                  className="bg-[#f2f2f2]"
                  value={formData.customFields?.[textFromNormalToCamelCase(field)] as string}
                  onChange={handleChangeCustomFields}
                />
              ))}
            </Fragment>
          )}
          <Fragment>
            <div className="mb-4">
              <Button
                className="
                  w-full flex items-center justify-center gap-3
                  text-2xl uppercase primary-button
                "
                onClick={handleClick}
                loading={loading}
                disabled={!requiredConsentsIds.every(c => formData.consents?.includes(c))}
              >
                {qwid && hasPublicData ? t('buttons.proceed') : t('buttons.register')}
              </Button>
            </div>
            {!!consentDocuments?.length && quiz.registrationPage?.showAgreeAllCheckbox && (
              <Checkbox
                id="checkAll"
                name="checkAll"
                checked={formData.consents?.length === consentDocuments?.length}
                onChange={handleChangeCheckbox}
              >
                <label
                  htmlFor="checkAll"
                  className="text-sm font-bold text-primary"
                >{t('pages.register.agreeAllTermsAndConditions')}</label>
              </Checkbox>
            )}
            <div className="pl-4 pt-4 mb-4">
              {consentDocuments?.map(document => (
                <Checkbox
                  key={document._id}
                  className="mb-2"
                  id={document._id}
                  name={document._id}
                  checked={formData.consents?.includes(document._id) || false}
                  onChange={handleChangeCheckbox}
                >
                  <label
                    htmlFor={document._id}
                    className="text-xs text-primary"
                  >
                    {document.required && (
                      <>
                        <span>*</span>
                        &nbsp;
                      </>
                    )}
                    {renderWithVariables(document.shortText, {
                      organizationName: quiz.organization
                    })}
                    &nbsp;&nbsp;
                    {document.content ? (
                      <span className="font-bold cursor-pointer" onClick={(e) => {
                        e.preventDefault();
                        handleOpenContentDialog(document.content);
                      }}>
                        {t('buttons.showMore')}
                      </span>
                    ) : (
                      <a className="font-bold cursor-pointer" href={document.url} target="_blank" rel="noreferrer">{t('buttons.showMore')}</a>
                    )}
                    &nbsp;
                  </label>
                </Checkbox>
              ))}
            </div>

            {!!consentLinksDocuments?.length ? (
              <div className="pl-4 mb-4">
                {consentLinksDocuments.map(document => {
                  if (document.content) return (
                    <p
                      key={`consent-link-${document._id}`}
                      className="font-bold cursor-pointer text-xs text-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        handleOpenContentDialog(document.content);
                      }}
                    >
                      {renderWithVariables(document.shortText, {
                        organizationName: quiz.organization
                      })}
                    </p>
                  )

                  return (
                    <a
                      key={`consent-link-${document._id}`}
                      className="font-bold cursor-pointer text-xs text-primary"
                      href={document.url}
                      target="_blank" rel="noreferrer"
                    >
                      {renderWithVariables(document.shortText, {
                        organizationName: quiz.organization
                      })}
                    </a>
                  )
                })}
              </div>
            ) : null}


            {quiz.registrationPage?.showRequiredHint ? (
              <p className="text-xs text-primary text-center">
                <Trans
                  t={t}
                  components={{ span: <span className="text-red-600" /> }}
                  i18nKey="pages.register.fieldsRequiredHint"
                />
              </p>
            ) : null}
          </Fragment>
        </div>
      )}
      {openContentDialog ? (
        <ConsentContentDialog onClose={toggleContentDialog} content={selectedContent} />
      ) : null}
    </Fragment>
  )
}

export default PublicForm;
